import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Minus } from "lucide-react";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

export default function ImageLightbox({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const drag = useRef(null);
  const moved = useRef(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const zoomTo = (next) => {
    const s = clamp(next, 1, 4);
    setScale(s);
    if (s === 1) setPos({ x: 0, y: 0 });
  };

  const onWheel = (e) => {
    e.preventDefault();
    zoomTo(scale + (e.deltaY < 0 ? 0.35 : -0.35));
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    moved.current = false;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale };
      drag.current = null;
    } else if (pointers.current.size === 1) {
      drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y, startX: e.clientX, startY: e.clientY };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      moved.current = true;
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      zoomTo(pinch.current.scale * (dist / pinch.current.dist));
    } else if (drag.current) {
      if (Math.hypot(e.clientX - drag.current.startX, e.clientY - drag.current.startY) > 6) moved.current = true;
      if (scale > 1) setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) drag.current = null;
  };

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget && !moved.current) onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] bg-ink/95 backdrop-blur-sm flex flex-col" data-testid="image-lightbox"
      onClick={onBackdropClick}>
      <button onClick={onClose} data-testid="lightbox-close" aria-label="Close zoom"
        className="absolute top-4 right-4 z-[82] w-12 h-12 bg-chalk text-ink hover:bg-pulse transition-colors flex items-center justify-center shadow-lg">
        <X size={22} />
      </button>
      <div className="flex items-center justify-between pl-5 pr-20 sm:pl-8 h-16 shrink-0" onClick={onBackdropClick}>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite">
          {Math.round(scale * 100)}% — scroll, pinch or double-tap to zoom · drag to move
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => zoomTo(scale - 0.5)} data-testid="lightbox-zoom-out" aria-label="Zoom out"
            className="w-10 h-10 border border-border bg-ink text-chalk/80 hover:border-chalk flex items-center justify-center"><Minus size={16} /></button>
          <button onClick={() => zoomTo(scale + 0.5)} data-testid="lightbox-zoom-in" aria-label="Zoom in"
            className="w-10 h-10 border border-border bg-ink text-chalk/80 hover:border-chalk flex items-center justify-center"><Plus size={16} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex items-center justify-center px-4 pb-6 touch-none select-none"
        data-testid="lightbox-backdrop"
        onWheel={onWheel} onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onClick={onBackdropClick}
        style={{ cursor: scale > 1 ? "grab" : "zoom-in" }}>
        <img src={src} alt={alt} draggable={false} data-testid="lightbox-image"
          onDoubleClick={() => zoomTo(scale > 1 ? 1 : 2.5)}
          className="max-h-full max-w-full bg-white"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, transition: pointers.current.size ? "none" : "transform 0.2s ease-out" }} />
      </div>
    </div>,
    document.body
  );
}
